import { useEffect, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { mappingNotificationApi } from "@services/mappingNotificationApi";
import { applyMappingMessage } from "@utils/builders/mapping";
import { MappingViewStatus, type IMappingJobViewModel } from "@models/mapping";

const POLL_INTERVAL_MS = 5000;
// Give up waiting if no mapping job has appeared within this window. The job
// is created asynchronously after extraction publishes its SQS trigger, so a
// short gap is normal; but a lost message / mapping that never starts must not
// leave the UI polling (or showing "starting…") forever.
const START_TIMEOUT_MS = 90_000;

export interface MappingJobState {
  /** The mapping job once it exists, else null. */
  job: IMappingJobViewModel | null;
  /** True while we still expect a job (the async-create gap) or one is
   *  running. False once terminal, after the start-timeout gives up, or when
   *  disabled — so the UI can show "starting…" without spinning forever. */
  waiting: boolean;
}

const isTerminal = (job: IMappingJobViewModel | null): boolean =>
  job?.status === MappingViewStatus.Completed ||
  job?.status === MappingViewStatus.Failed;

export const useMappingJob = (
  processId: string,
  processItemId: string,
  enabled: boolean = true,
): MappingJobState => {
  const [job, setJob] = useState<IMappingJobViewModel | null>(null);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (!enabled || !processId || !processItemId) {
      setJob(null);
      setWaiting(false);
      return;
    }

    let cancelled = false;
    let pollTimer: number | null = null;
    let startTimeout: number | null = null;
    let unsubscribe: (() => void) | null = null;
    // Latest job seen, tracked outside React state so the start-timeout can
    // synchronously decide whether anything ever arrived.
    let current: IMappingJobViewModel | null = null;

    setWaiting(true);

    const stopAll = (): void => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      if (startTimeout !== null) {
        clearTimeout(startTimeout);
        startTimeout = null;
      }
      unsubscribe?.();
      unsubscribe = null;
    };

    const settle = (vm: IMappingJobViewModel | null): void => {
      current = vm;
      if (cancelled) return;
      setJob(vm);
      if (isTerminal(vm)) {
        setWaiting(false);
        stopAll();
      }
    };

    const fetchOnce = async (): Promise<IMappingJobViewModel | null> => {
      try {
        // 200 with a null body ("no job yet") is the normal async-create gap,
        // not an error — the API no longer 404s here.
        const vm = await mappingApi.getMappingJobByProcessItem(
          processId,
          processItemId,
        );
        settle(vm);
        return vm;
      } catch (error) {
        console.error(
          `Failed to fetch mapping job for item ${processItemId}:`,
          error,
        );
        return null;
      }
    };

    const startPolling = (): void => {
      if (pollTimer !== null) return;
      pollTimer = window.setInterval(() => {
        void fetchOnce();
      }, POLL_INTERVAL_MS);
    };

    // Bounded wait: if no job ever shows up, stop watching and clear "waiting".
    startTimeout = window.setTimeout(() => {
      if (cancelled) return;
      if (current === null) {
        setWaiting(false);
        stopAll();
      }
    }, START_TIMEOUT_MS);

    void (async () => {
      const initial = await fetchOnce();
      if (cancelled || isTerminal(initial)) {
        return;
      }
      try {
        await mappingNotificationApi.connect();
        if (cancelled) return;
        unsubscribe = mappingNotificationApi.subscribe(
          processItemId,
          (message) => {
            if (cancelled) return;
            setJob((prev) => {
              const next = applyMappingMessage(prev, message);
              current = next;
              if (isTerminal(next)) {
                setWaiting(false);
                stopAll();
              }
              return next;
            });
          },
        );
      } catch (error) {
        console.warn(
          "Mapping notification socket unavailable, falling back to polling:",
          error,
        );
        if (!cancelled) {
          startPolling();
        }
      }
    })();

    return () => {
      cancelled = true;
      stopAll();
    };
  }, [processId, processItemId, enabled]);

  return { job, waiting };
};
